import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import CopyButton from './CopyButton';

// ─── Parse Mode 3 (bulk) description into per-photo listing blocks ────────────
function parseMode3Listings(description) {
  const blocks = [];
  const parts = description.split(/(?=\bPhoto\s+\d+\s*:)/i);

  for (const part of parts) {
    const m = part.match(/^Photo\s+(\d+)\s*:\s*([^\n]+)?\n?([\s\S]*)/i);
    if (!m) continue;
    const num      = parseInt(m[1], 10);
    const miniTitle = (m[2] || '').trim();
    const body      = (m[3] || '').trim();
    blocks.push({
      photoIndex: num - 1,
      num,
      label: `Photo ${num}`,
      miniTitle,
      body,
      fullText: miniTitle ? `${miniTitle}\n\n${body}` : body,
    });
  }

  if (blocks.length === 0) {
    blocks.push({ photoIndex: 0, num: 1, label: 'Listing 1', miniTitle: '', body: description, fullText: description });
  }

  return blocks;
}

// ─── RefinePanel — text input + button shown below the result cards ───────────
function RefinePanel({ listingId, token, originalText, onRefined }) {
  const [correction, setCorrection] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);

  const handleRefineListing = useCallback(async () => {
    if (!correction.trim() || isRefining) return;
    setIsRefining(true);
    setRefineError(null);

    try {
      const res = await fetch('/api/listings/refine', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          listingId,
          originalText,
          userCorrection: correction.trim(),
        }),
      });

      const data = await res.json();

      if (!data.success) {
        setRefineError(data.error?.message || 'Refinement failed. Please try again.');
        return;
      }

      onRefined({ title: data.data.title, description: data.data.description });
      setCorrection('');
    } catch {
      setRefineError('Something went wrong. Please try again.');
    } finally {
      setIsRefining(false);
    }
  }, [correction, isRefining, listingId, token, originalText, onRefined]);

  return (
    <div className="refine-panel">
      <p className="refine-panel__label">AI missed something?</p>
      <textarea
        className="refine-panel__input"
        value={correction}
        onChange={(e) => setCorrection(e.target.value)}
        placeholder="Type corrections here (e.g. 'Bedframe only, no mattress' or 'Spezial, not Sambas')…"
        rows={3}
        disabled={isRefining}
      />
      {refineError && (
        <p className="refine-panel__error">{refineError}</p>
      )}
      <button
        className="refine-panel__btn"
        onClick={handleRefineListing}
        disabled={!correction.trim() || isRefining}
      >
        {isRefining ? (
          <>
            <span className="refine-panel__spinner" aria-hidden="true" />
            Refining…
          </>
        ) : (
          'Refine Listing'
        )}
      </button>
    </div>
  );
}

// ─── ListingResult ────────────────────────────────────────────────────────────
// Props:
//   listing        — { title, description } from API
//   images         — File[] uploaded by the user (still in memory post-generation)
//   listingMode    — 1 | 2 | 3
//   activeIndex    — carousel index (controlled by parent)
//   onIndexChange  — (newIndex) => void
//   listingId      — DB id of the saved listing (for refine API)
//   token          — JWT bearer token
//   onListingRefined — ({ title, description }) => void
export default function ListingResult({ listing, images = [], listingMode = 1, activeIndex = 0, onIndexChange, listingId, token, onListingRefined }) {
  const imageUrls = useMemo(() => images.map((f) => URL.createObjectURL(f)), [images]);
  useEffect(() => () => imageUrls.forEach((u) => URL.revokeObjectURL(u)), [imageUrls]);

  if (!listing) return null;
  const { title, description } = listing;

  // ── Mode 3: Carousel ────────────────────────────────────────────────────────
  if (listingMode === 3) {
    const items = parseMode3Listings(description);
    return (
      <CarouselResult
        overallTitle={title}
        items={items}
        imageUrls={imageUrls}
        activeIndex={activeIndex}
        onIndexChange={onIndexChange}
        listingId={listingId}
        token={token}
        onListingRefined={onListingRefined}
      />
    );
  }

  // ── Modes 1 & 2: Single / Bundle ────────────────────────────────────────────
  const fullText = `${title}\n\n${description}`;
  return (
    <div className="result">
      {imageUrls.length > 0 && (
        <PhotoGallery imageUrls={imageUrls} showBadges={listingMode === 2} />
      )}

      <ResultCard label="Title" copyText={title}>
        <p className="result__title">{title}</p>
      </ResultCard>

      <ResultCard
        label={listingMode === 2 ? 'Bundle Description' : 'Description'}
        copyText={description}
      >
        <p className="result__description">{description}</p>
      </ResultCard>

      {listingId && token && onListingRefined && (
        <RefinePanel
          listingId={listingId}
          token={token}
          originalText={fullText}
          onRefined={onListingRefined}
        />
      )}

      <div className="result__cta-block">
        <p className="result__cta-hint">
          {listingMode === 2
            ? 'Copy the full bundle listing then paste into Facebook Marketplace.'
            : 'Copy everything, then paste directly into Facebook Marketplace.'}
        </p>
        <CopyButton
          textToCopy={fullText}
          openUrl="https://www.facebook.com/marketplace/create/item"
          label="Copy & Open Facebook"
          variant="primary"
        />
      </div>

      <ListingStyles />
    </div>
  );
}

// ─── PhotoGallery — horizontal scroll strip for modes 1 & 2 ──────────────────
function PhotoGallery({ imageUrls, showBadges }) {
  return (
    <div className="photo-gallery">
      <div className="photo-gallery__strip">
        {imageUrls.map((url, i) => (
          <div key={i} className={`photo-thumb ${i === 0 && !showBadges ? 'photo-thumb--primary' : ''}`}>
            <img src={url} alt={`Photo ${i + 1}`} className="photo-thumb__img" loading="eager" />
            {showBadges && (
              <span className="photo-thumb__badge">Photo {i + 1}</span>
            )}
            {!showBadges && i === 0 && (
              <span className="photo-thumb__badge photo-thumb__badge--subtle">Main</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CarouselResult — one listing at a time for mode 3 ───────────────────────
function CarouselResult({ overallTitle, items, imageUrls, activeIndex, onIndexChange, listingId, token, onListingRefined }) {
  const count    = items.length;
  const safeIdx  = Math.min(activeIndex, count - 1);
  const item     = items[safeIdx];
  const photoUrl = imageUrls[item?.photoIndex] ?? imageUrls[0];
  const touchX   = useRef(null);

  const goPrev = () => safeIdx > 0         && onIndexChange(safeIdx - 1);
  const goNext = () => safeIdx < count - 1 && onIndexChange(safeIdx + 1);

  const handleTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 48) dx < 0 ? goNext() : goPrev();
    touchX.current = null;
  };

  const fullText = item ? (item.miniTitle ? `${item.miniTitle}\n\n${item.body}` : item.body) : '';

  return (
    <div className="result carousel">
      {/* ── Full-width photo */}
      <div
        className="carousel__photo-wrap"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {photoUrl
          ? <img key={safeIdx} src={photoUrl} alt={item.label} className="carousel__photo" />
          : <div className="carousel__photo-empty">📷</div>
        }
        <div className="carousel__counter-pill">
          {safeIdx + 1} / {count}
        </div>
      </div>

      {/* ── Prev / dots / Next */}
      <div className="carousel__nav">
        <button
          className="carousel__nav-btn"
          onClick={goPrev}
          disabled={safeIdx === 0}
          aria-label="Previous listing"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>

        <div className="carousel__dots" aria-label="Listings">
          {items.map((_, i) => (
            <button
              key={i}
              className={`carousel__dot ${i === safeIdx ? 'carousel__dot--active' : ''}`}
              onClick={() => onIndexChange(i)}
              aria-label={`Go to listing ${i + 1}`}
            />
          ))}
        </div>

        <button
          className="carousel__nav-btn"
          onClick={goNext}
          disabled={safeIdx === count - 1}
          aria-label="Next listing"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      {/* ── Listing content card */}
      <div className="result-card" key={`listing-${safeIdx}`}>
        <div className="result-card__header">
          <span className="result-card__label">Listing {item.num} of {count}</span>
          <CopyButton textToCopy={item.fullText} variant="ghost" label="Copy" />
        </div>
        {item.miniTitle && <p className="result__title">{item.miniTitle}</p>}
        <p className="result__description">{item.body}</p>
      </div>

      {listingId && token && onListingRefined && (
        <RefinePanel
          listingId={listingId}
          token={token}
          originalText={fullText}
          onRefined={onListingRefined}
        />
      )}

      {/* ── Per-item CTA */}
      <div className="result__cta-block">
        <p className="result__cta-hint">
          Listing {item.num} of {count} — copy and paste into Facebook Marketplace,
          then use the arrows to move to the next item.
        </p>
        <CopyButton
          textToCopy={item.fullText}
          openUrl="https://www.facebook.com/marketplace/create/item"
          label={`Copy Listing ${item.num} & Open Facebook`}
          variant="primary"
        />
      </div>

      <ListingStyles />
    </div>
  );
}

// ─── ResultCard ───────────────────────────────────────────────────────────────
function ResultCard({ label, copyText, children }) {
  return (
    <div className="result-card">
      <div className="result-card__header">
        <span className="result-card__label">{label}</span>
        <CopyButton textToCopy={copyText} variant="ghost" label="Copy" />
      </div>
      {children}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
function ListingStyles() {
  return (
    <style>{`
      .result  { display: flex; flex-direction: column; gap: 12px; }
      .carousel { display: flex; flex-direction: column; gap: 12px; }

      /* ── Photo gallery strip (modes 1 & 2) */
      .photo-gallery {
        overflow-x: auto; -webkit-overflow-scrolling: touch;
        margin: 0 -20px; padding: 0 20px;
        scrollbar-width: none;
      }
      .photo-gallery::-webkit-scrollbar { display: none; }

      .photo-gallery__strip { display: flex; gap: 8px; padding-right: 24px; }

      .photo-thumb {
        position: relative; flex: 0 0 auto;
        width: 96px; height: 72px;
        border-radius: 8px; overflow: hidden;
        background: var(--color-bg-page);
        border: 0.5px solid var(--color-border);
      }
      .photo-thumb--primary { border-color: var(--color-primary); }
      .photo-thumb__img { width: 100%; height: 100%; object-fit: cover; display: block; }

      .photo-thumb__badge {
        position: absolute; top: 5px; left: 5px;
        font-size: 9px; font-weight: 500; letter-spacing: 0.03em;
        background: rgba(15,81,50,0.82); color: #EAF3DE;
        padding: 2px 6px; border-radius: 9999px;
        backdrop-filter: blur(4px); white-space: nowrap;
      }
      .photo-thumb__badge--subtle {
        background: rgba(0,0,0,0.4); color: rgba(255,255,255,0.88);
      }

      /* ── Result cards */
      .result-card {
        background: var(--color-bg-card); border: 0.5px solid var(--color-border);
        border-radius: var(--radius-lg); padding: 14px 16px;
        display: flex; flex-direction: column; gap: 8px;
      }
      .result-card__header { display: flex; align-items: center; justify-content: space-between; }
      .result-card__label {
        font-size: 10px; font-weight: 500; letter-spacing: 0.08em;
        text-transform: uppercase; color: var(--color-text-hint);
      }
      .result__title { font-size: 15px; font-weight: 500; color: var(--color-text-heading); line-height: 1.4; }
      .result__description { font-size: 13px; color: var(--color-text-secondary); line-height: 1.65; white-space: pre-wrap; }

      /* ── Refine panel */
      .refine-panel {
        display: flex; flex-direction: column; gap: 10px;
        background: var(--color-bg-card); border: 0.5px solid var(--color-border);
        border-radius: var(--radius-lg); padding: 14px 16px;
      }
      .refine-panel__label {
        font-size: 10px; font-weight: 500; letter-spacing: 0.08em;
        text-transform: uppercase; color: var(--color-text-hint);
      }
      .refine-panel__input {
        width: 100%; box-sizing: border-box;
        background: var(--color-bg-page); border: 0.5px solid var(--color-border);
        border-radius: 10px; padding: 10px 12px;
        font-size: 13px; color: var(--color-text-primary); line-height: 1.55;
        resize: none; outline: none; font-family: inherit;
        transition: border-color 0.15s;
      }
      .refine-panel__input::placeholder { color: var(--color-text-hint); }
      .refine-panel__input:focus { border-color: var(--color-primary); }
      .refine-panel__input:disabled { opacity: 0.55; }
      .refine-panel__error {
        font-size: 12px; color: #e05050; line-height: 1.5;
      }
      .refine-panel__btn {
        align-self: flex-end;
        display: inline-flex; align-items: center; gap: 8px;
        background: var(--color-primary); color: #fff;
        border: none; border-radius: 10px;
        padding: 9px 18px; font-size: 13px; font-weight: 500;
        cursor: pointer; transition: opacity 0.15s, background 0.15s;
        -webkit-tap-highlight-color: transparent;
      }
      .refine-panel__btn:disabled { opacity: 0.5; pointer-events: none; }
      .refine-panel__btn:hover:not(:disabled) { opacity: 0.88; }
      .refine-panel__spinner {
        width: 13px; height: 13px; border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.35);
        border-top-color: #fff;
        animation: refine-spin 0.7s linear infinite;
        flex-shrink: 0;
      }
      @keyframes refine-spin { to { transform: rotate(360deg); } }

      .result__cta-block {
        display: flex; flex-direction: column; gap: 12px; margin-top: 4px;
        padding: 16px; background: var(--color-bg-card);
        border: 0.5px solid var(--color-border); border-radius: var(--radius-lg);
      }
      .result__cta-hint { font-size: 12px; color: var(--color-text-hint); text-align: center; line-height: 1.6; }

      /* ── Carousel photo */
      .carousel__photo-wrap {
        position: relative; border-radius: var(--radius-lg); overflow: hidden;
        aspect-ratio: 4/3; background: var(--color-bg-page);
        user-select: none; -webkit-user-select: none;
        touch-action: pan-y;
      }
      .carousel__photo {
        width: 100%; height: 100%; object-fit: cover; display: block;
        animation: cfade 0.22s ease;
      }
      .carousel__photo-empty {
        width: 100%; height: 100%; display: flex; align-items: center;
        justify-content: center; font-size: 48px;
      }
      @keyframes cfade { from { opacity: 0.55; } to { opacity: 1; } }

      .carousel__counter-pill {
        position: absolute; top: 10px; right: 10px;
        background: rgba(0,0,0,0.42); color: rgba(255,255,255,0.92);
        font-size: 11px; font-weight: 500; padding: 3px 9px;
        border-radius: 9999px; backdrop-filter: blur(4px);
      }

      /* ── Carousel navigation */
      .carousel__nav {
        display: flex; align-items: center; justify-content: space-between;
        padding: 0 2px;
      }
      .carousel__nav-btn {
        width: 36px; height: 36px; border-radius: 50%;
        background: var(--color-bg-card); border: 0.5px solid var(--color-border);
        color: var(--color-text-secondary);
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; transition: background 0.15s, opacity 0.15s;
        -webkit-tap-highlight-color: transparent;
      }
      .carousel__nav-btn:hover:not(:disabled) { background: var(--color-primary-light); border-color: var(--color-primary); color: var(--color-primary); }
      .carousel__nav-btn:disabled { opacity: 0.22; pointer-events: none; }

      .carousel__dots { display: flex; align-items: center; gap: 7px; }
      .carousel__dot {
        width: 7px; height: 7px; border-radius: 50%; border: none; padding: 0;
        background: var(--color-border-dashed); cursor: pointer;
        transition: background 0.18s, transform 0.18s;
        -webkit-tap-highlight-color: transparent;
      }
      .carousel__dot--active { background: var(--color-primary); transform: scale(1.35); }
    `}</style>
  );
}
